import { Palette } from "../ui/palette";
import type { PortraitKey } from "../ui/portraits";

/**
 * Story cutscenes: the old-video-game dialogue screens that bookend the game and
 * play between levels. Everything here is data - to change what the founders say,
 * add a beat, or add a whole new cutscene, edit this file only; DialogueScene
 * renders whatever it's handed.
 *
 * Two beat styles, deliberately distinct on screen (see DialogueScene):
 *   narration - the storyteller voice. No portrait, centred, warm/gold, italic.
 *   line      - someone speaking. Framed portrait on their side + a name plate +
 *               their words, typed out one character at a time.
 */

export type SpeakerId = "dan" | "lucas" | "andrew" | "kirby";

export interface Speaker {
  name: string;
  /** Portrait texture key (loaded in PreloadScene from assets/portraits/). */
  tex: string;
  /** Which side of the screen this person's portrait sits on. Keeping each person
   *  on a fixed side means a back-and-forth reads as a real conversation. */
  side: "left" | "right";
  /** Accent colour for the name plate + portrait frame. */
  color: number;
}

export const SPEAKERS: Record<SpeakerId, Speaker> = {
  lucas: { name: "Lukas", tex: "cutscene-lucas", side: "left", color: Palette.lucas },
  dan: { name: "Dan", tex: "cutscene-dan", side: "right", color: Palette.dan },
  andrew: { name: "Andrew", tex: "cutscene-andrew", side: "right", color: Palette.andrew },
  kirby: { name: "Kirby", tex: "cutscene-kirby", side: "left", color: Palette.kirby },
};

/** Every portrait texture the cutscene system needs preloaded. */
export const CUTSCENE_PORTRAITS = Object.values(SPEAKERS).map((s) => s.tex);

export type Beat =
  | { kind: "narration"; text: string }
  | { kind: "line"; speaker: SpeakerId; text: string };

export interface Cutscene {
  beats: Beat[];
}

export type CutsceneKey = "intro" | "post-dan" | "post-lucas" | "post-andrew" | "pre-relay" | "post-relay";

export const CUTSCENES: Record<CutsceneKey, Cutscene> = {
  // Plays before the start screen: how the whole thing began.
  intro: {
    beats: [
      { kind: "narration", text: "A long time ago, in an age before AI..." },
      { kind: "line", speaker: "dan", text: "Lukas, my man. Good to see you again. How's work?" },
      { kind: "line", speaker: "lucas", text: "Sux..." },
      { kind: "line", speaker: "lucas", text: "You know, we should build something together." },
      { kind: "line", speaker: "dan", text: "Dude, you read my mind! Start next week?" },
      { kind: "narration", text: "And so began the legend of..." },
    ],
  },

  // After the Dan level: the grind pays off and needs a system.
  "post-dan": {
    beats: [
      { kind: "narration", text: "The deals kept coming, and so did the paperwork." },
      { kind: "line", speaker: "dan", text: "We're doing a ton of Tech DDs now. We need a strategy." },
      { kind: "line", speaker: "dan", text: "(opens Excel)" },
      { kind: "line", speaker: "lucas", text: "I love the energy Dan, but this article I found on LinkedIn should have all the answers." },
      { kind: "line", speaker: "dan", text: "Sick! Go to work my man!" },
      { kind: "narration", text: "And so, it was up to Lukas to piece together the strategy." },
      { kind: "line", speaker: "dan", text: "Hey, I helped!" },
      { kind: "narration", text: "And so, it was up to Lukas to piece together the strategy. And Dan also helped. (edited)" },
    ],
  },

  // After the Lucas level: Bionic exists, now it needs to be built for real.
  "post-lucas": {
    beats: [
      { kind: "narration", text: "What a milestone! Dan and Lukas's tech due dilligence was now powered by the Bionic strategy." },
      { kind: "narration", text: "But everything still felt quite manual. The next step was automation and scale." },
      { kind: "line", speaker: "lucas", text: "Hey Dan, do you know any smart, capable, well-rounded engineers?" },
      { kind: "line", speaker: "dan", text: "No. But this guy Andrew says he is available today." },
      { kind: "line", speaker: "andrew", text: "Hey guys, super jazed about the role. I just have two conditions:" },
      { kind: "line", speaker: "andrew", text: "1. We don't use Microsoft Teams, Node JS, or have to do ISO security stuff." },
      { kind: "line", speaker: "andrew", text: "2. You never hire my nemisis, Quinn Goh." },
      { kind: "line", speaker: "dan", text: "That's four conditions. And no promises to any of that." },
      { kind: "line", speaker: "andrew", text: "..." },
      { kind: "line", speaker: "andrew", text: "lgtm 🚀" },
      { kind: "narration", text: "Bionic was about to become feature rich and bug free." },
    ],
  },

  // After the Lucas level: Bionic exists, now it needs to be built for real.
  "post-andrew": {
    beats: [
      { kind: "narration", text: "With all feature requests handled and every bug squashed, Bionic was ready." },
    ],
  },

  // Before the Kirby relay (the American market): the founders decide to go for
  // America. This dialogue leads straight into Kirby's video call (KirbyCallScene).
  "pre-relay": {
    beats: [
      { kind: "line", speaker: "lucas", text: "Wow, it feels great to have conquered Europe. What should we do next?" },
      { kind: "line", speaker: "dan", text: "Let's take on America." },
      { kind: "line", speaker: "lucas", text: "We are going to need someone specfic." },
      { kind: "line", speaker: "lucas", text: "I'm thinking the versatility of an F-150, the charm of a Ranger, and the energy of a Cybertruck." },
      { kind: "line", speaker: "dan", text: "Kirby!" },
      { kind: "line", speaker: "dan", text: "Let me call him..." },
    ],
  },

  "post-relay": {
    beats: [
      { kind: "narration", text: "And so their destiny was set." },
      { kind: "narration", text: "An overnight success, 10 years in the making, they could now celebrate on the island of Mallorca..." },
    ],
  },
};

/** One line Kirby speaks on his video call, paired with the portrait animation to
 *  play while he says it. Data-driven so the script + animations are easy to tweak. */
export interface KirbyCallBeat {
  anim: PortraitKey;
  text: string;
  /** false = play the animation once and hold; default loops while the line shows. */
  loop?: boolean;
  /** ms to hold between loops of the animation (e.g. a pause between each wave).
   *  0/undefined = loop continuously. */
  repeatDelayMs?: number;
}

/** Kirby's video call, shown after the pre-relay dialogue and before the relay.
 *  Opens on a "Connecting..." screen, then Kirby comes on and runs these beats. */
export const KIRBY_CALL: { connectingMs: number; beats: KirbyCallBeat[] } = {
  connectingMs: 1800,
  beats: [
    { anim: "kirby-wave", text: "Hey guys it's been too long. Good to see you.", repeatDelayMs: 5000 },
    { anim: "kirby-talk", text: "Okay so you say you want to take on America next?", loop: false },
    { anim: "kirby-drink", text: "mmhmm", loop: false },
    { anim: "kirby-laugh", text: "Ha ha ha!", repeatDelayMs: 2000 },
    { anim: "kirby-oh", text: "Oh. You're serious.", loop: false },
    { anim: "kirby-talk", text: "Okay. America's a whole different ballgame. But I know the right people to open the doors.", loop: false },
    { anim: "kirby-cross-arms", text: "First though, you have to show me what you've got.", loop: false },
  ],
};

/** The post-level cutscene to play when a level is beaten, if any. */
export const POST_LEVEL_CUTSCENE: Record<string, CutsceneKey | undefined> = {
  dan: "post-dan",
  lucas: "post-lucas",
  andrew: "post-andrew",
  relay: "post-relay",
};
